import React, { useState, useEffect } from 'react';
import { useTheme as useMuiTheme, ThemeProvider } from '@mui/material/styles';
import 'bootstrap/dist/css/bootstrap.min.css';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Spinner,
  ListGroup,
  Badge,
  Modal,
  OverlayTrigger,
  Tooltip,
  FloatingLabel,
  Fade,
  Alert,
  Stack,
  Nav,
  Tab
} from 'react-bootstrap';
import { 
  TbArrowsExchange,
  TbUsersGroup,
  TbUserPlus,
  TbTrash,
  TbUserMinus,
  TbInfoCircle 
} from "react-icons/tb";
import { 
  FiUserPlus,
  FiUsers,
  FiUser,
  FiMail,
  FiKey,
  FiClock,
  FiRefreshCw,
  FiTrash2,
  FiInfo
} from "react-icons/fi";
import { getAdminToken } from '../services/authService';
import { useTheme } from "../ThemeContext";
import { useMediaQuery } from '@mui/material';

function UserManagement() {
  const [activeTab, setActiveTab] = useState('admins');
  const { isDarkMode } = useTheme();
  const isMobile = useMediaQuery("(max-width:900px)");

  // Admin Management State
  const [adminFormData, setAdminFormData] = useState({ 
    name: "", 
    email: "", 
    password: "" 
  });
  const [admins, setAdmins] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminFetching, setAdminFetching] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminSuccess, setAdminSuccess] = useState(null);
  const [showDeleteAdminModal, setShowDeleteAdminModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);

  // Team Management State
  const [team, setTeam] = useState({ name: '', description: '' });
  const [member, setMember] = useState({ name: '', email: '', role: '' });
  const [teams, setTeams] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showDeleteTeamModal, setShowDeleteTeamModal] = useState(false);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  const token = getAdminToken();

  const theme = useMuiTheme();

  // Theme styles
  const themeStyles = {
    backgroundColor: "var(--v2-bg)",
    color: "var(--v2-text)",
    minHeight: 0,
    padding: 0
  };

  const cardThemeStyles = {
    backgroundColor: "var(--v2-surface)",
    color: "var(--v2-text)",
    border: 'none',
    borderRadius: '12px',
    // boxShadow: isDarkMode 
    //   ? '0 4px 20px rgba(0, 0, 0, 0.25)' 
    //   : '0 4px 20px rgba(0, 0, 0, 0.05)',
    marginBottom: isMobile ? "1rem" : "1.5rem"
  };

  const inputThemeStyles = {
    backgroundColor: "var(--v2-bg)",
    color: "var(--v2-text)",
    borderColor: "var(--v2-border-strong)",
    borderRadius: '8px',
    ':focus': {
      boxShadow: `0 0 0 0.25rem var(--v2-accent-subtle)`,
      borderColor: "var(--v2-accent)",
    }
  };

  // Button hover effect styles
  const buttonHoverStyle = {
    transition: 'transform 0.2s ease',
    ':hover': {
      transform: 'scale(1.02)'
    },
    ':active': {
      transform: 'scale(0.98)'
    }
  };

  // Admin Management Functions
  const handleAdminChange = (e) => {
    setAdminFormData({ ...adminFormData, [e.target.name]: e.target.value });
  };

  const fetchAdmins = async () => {
    try {
      setAdminFetching(true);
      setAdminError(null);
      const response = await fetch("/s1/admin/admins", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to fetch admins");
      setAdmins(data);
    } catch (error) {
      setAdminError(error.message || "Error fetching admins");
    } finally {
      setAdminFetching(false);
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (!adminFormData.name || !adminFormData.email || !adminFormData.password) {
      setAdminError("Please fill all fields");
      return;
    }

    setAdminLoading(true);
    setAdminError(null);
    setAdminSuccess(null);

    try {
      const response = await fetch("/s1/admin/create-admin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(adminFormData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to create admin");

      setAdminSuccess("Admin created successfully!");
      setAdminFormData({ name: "", email: "", password: "" });
      fetchAdmins();
    } catch (error) {
      setAdminError(error.message || "Something went wrong.");
    } finally {
      setAdminLoading(false);
    }
  };

  const promptDeleteAdmin = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteAdminModal(true);
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;
    
    try {
      setAdminLoading(true);
      const response = await fetch(`/s1/admin/delete-admin/${adminToDelete.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to delete admin");

      setAdminSuccess("Admin deleted successfully!");
      fetchAdmins();
    } catch (error) {
      setAdminError(error.message || "Failed to delete admin");
    } finally {
      setAdminLoading(false);
      setShowDeleteAdminModal(false);
    }
  };

  // Team Management Functions
  const fetchTeams = async () => {
    try {
      const response = await fetch('/s1/admin/teams-with-members', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch teams');
      setTeams(data);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setTeamLoading(true);
    try {
      const response = await fetch('/s1/admin/teams', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(team),
      });

      if (!response.ok) throw new Error('Failed to create team');
      const data = await response.json();
      fetchTeams();
      toast.success(`Team "${data.name}" created!`);
      setTeam({ name: '', description: '' });
    } catch (err) {
      toast.error(err.message);
    }
    setTeamLoading(false);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedTeam) return;
    setTeamLoading(true);
    try {
      const response = await fetch(`/s1/admin/teams/${selectedTeam}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(member),
      });

      if (!response.ok) throw new Error('Failed to add member');
      toast.success('Member added successfully!');
      fetchTeams();
      setMember({ name: '', email: '', role: '' });
      setShowAddMemberModal(false);
    } catch (err) {
      toast.error(err.message);
    }
    setTeamLoading(false);
  };

  const handleRemoveMember = async () => {
    if (!selectedTeam || !selectedMember) return;
    setTeamLoading(true);
    try {
      const response = await fetch(`/s1/admin/teams/${selectedTeam}/members/${selectedMember.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to remove member');
      toast.success('Member removed');
      fetchTeams();
      setShowRemoveMemberModal(false);
    } catch (err) {
      toast.error(err.message);
    }
    setTeamLoading(false);
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    setTeamLoading(true);
    try {
      const response = await fetch(`/s1/admin/teams/${selectedTeam}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete team');
      toast.success('Team deleted');
      fetchTeams();
      setShowDeleteTeamModal(false);
    } catch (err) {
      toast.error(err.message);
    }
    setTeamLoading(false);
  };

  const openAddMemberModal = (teamId) => {
    setSelectedTeam(teamId);
    setShowAddMemberModal(true);
  };

  const openDeleteTeamModal = (teamId) => {
    setSelectedTeam(teamId);
    setShowDeleteTeamModal(true);
  };

  const openRemoveMemberModal = (teamId, member) => {
    setSelectedTeam(teamId);
    setSelectedMember(member);
    setShowRemoveMemberModal(true);
  };

  useEffect(() => {
    fetchAdmins();
    fetchTeams();
  }, []);

  const renderAdminManagement = () => (
    <Container fluid={isMobile ? true : "lg"} className="py-4" style={themeStyles}>
      <Row className="justify-content-center g-4">
        <Col xs={12}>
          {/* Create Admin Card */}
          <Card className="shadow-sm mb-4" style={cardThemeStyles}>
            <Card.Body className={isMobile ? "p-3" : "p-4"}>
              <Stack direction="horizontal" className="mb-4">
                <FiUserPlus size={isMobile ? 20 : 24} className="me-2" />
                <h4 className="mb-0" style={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>Create New Admin</h4>
              </Stack>

              {adminError && (
                <Alert variant="danger" className="mb-4" dismissible onClose={() => setAdminError(null)}>
                  {adminError}
                </Alert>
              )}
              {adminSuccess && (
                <Alert variant="success" className="mb-4" dismissible onClose={() => setAdminSuccess(null)}>
                  {adminSuccess}
                </Alert>
              )}

              <Form onSubmit={handleAdminSubmit}>
                <FloatingLabel 
                  controlId="name" 
                  label="Admin Name" 
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    name="name"
                    value={adminFormData.name}
                    onChange={handleAdminChange}
                    required
                    style={inputThemeStyles}
                    placeholder=" "
                    size={isMobile ? "sm" : undefined}
                  />
                </FloatingLabel>

                <FloatingLabel 
                  controlId="email" 
                  label="Email Address" 
                  className="mb-3"
                >
                  <Form.Control
                    type="email"
                    name="email"
                    value={adminFormData.email}
                    onChange={handleAdminChange}
                    required
                    style={inputThemeStyles}
                    placeholder=" "
                    size={isMobile ? "sm" : undefined}
                  />
                </FloatingLabel>

                <FloatingLabel 
                  controlId="password" 
                  label="Password"
                  className="mb-4"
                >
                  <Form.Control
                    type="password"
                    name="password"
                    value={adminFormData.password}
                    onChange={handleAdminChange}
                    required
                    style={inputThemeStyles}
                    placeholder=" "
                    size={isMobile ? "sm" : undefined}
                  />
                </FloatingLabel>

                <Button 
                  type="submit" 
                  variant="primary"
                  size={isMobile ? "sm" : "lg"}
                  className="w-100 py-2"
                  disabled={adminLoading}
                >
                  {adminLoading ? (
                    <Spinner size="sm" animation="border" className="me-2" />
                  ) : (
                    <FiUserPlus className="me-2" />
                  )}
                  Create Admin
                </Button>
              </Form>
            </Card.Body>
          </Card>

          {/* Admin List Card */}
          <Card className="shadow-sm" style={cardThemeStyles}>
            <Card.Body className={isMobile ? "p-3" : "p-4"}>
              <Stack direction="horizontal" className="mb-4">
                <FiUsers size={isMobile ? 20 : 24} className="me-2" />
                <h4 className="mb-0" style={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>Admin Accounts</h4>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Refresh list</Tooltip>}
                >
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="ms-auto p-2"
                    onClick={fetchAdmins}
                    disabled={adminFetching}
                  >
                    <FiRefreshCw size={isMobile ? 16 : 18} />
                  </Button>
                </OverlayTrigger>
              </Stack>

              {adminFetching && !admins.length ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant={isDarkMode ? "light" : "primary"} />
                </div>
              ) : admins.length > 0 ? (
                <ListGroup variant="flush" className="rounded-3 overflow-hidden">
                  {admins.map((admin, index) => (
                    <ListGroup.Item 
                      key={admin.id} 
                      style={{
                        backgroundColor: "var(--v2-surface)",
                        borderColor: "var(--v2-border)",
                        padding: isMobile ? '0.75rem' : '1rem'
                      }}
                    >
                      <Stack direction="horizontal" gap={isMobile ? 2 : 3}>
                        <div className="d-flex align-items-center">
                          <Badge 
                            bg="secondary" 
                            className="me-2" 
                            style={{ 
                              width: isMobile ? '28px' : '32px', 
                              height: isMobile ? '28px' : '32px', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              borderRadius: '50%',
                              fontSize: isMobile ? '0.8rem' : '0.9rem'
                            }}
                          >
                            {index + 1}
                          </Badge>
                        </div>
                        <div className="flex-grow-1" style={{ overflow: 'hidden' }}>
                          <div className={`${isDarkMode?"text-light":"text-dark"} fw-medium mb-1`} style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                            <FiUser size={isMobile ? 14 : 16} className="me-2" />
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {admin.username}
                            </span>
                          </div>
                          <div className={`${isDarkMode?'text-light':'text-muted'} small d-flex align-items-center mb-1`} style={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                            <FiMail size={isMobile ? 12 : 14} className="me-2" />
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {admin.email}
                            </span>
                          </div>
                          <div className={`${isDarkMode?"text-light":"text-muted"} small d-flex align-items-center`} style={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                            <FiClock size={isMobile ? 12 : 14} className="me-2" />
                            Created: {new Date(admin.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Delete admin</Tooltip>}
                        >
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className="p-1 p-sm-2"
                            onClick={() => promptDeleteAdmin(admin)}
                            disabled={adminLoading}
                          >
                            <FiTrash2 size={isMobile ? 14 : 16} />
                          </Button>
                        </OverlayTrigger>
                      </Stack>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FiInfo size={isMobile ? 24 : 32} className="mb-3" />
                  <p className={isDarkMode?"text-light mb-0":"mb-0"}>No admin accounts found</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );

  const renderTeamManagement = () => (
    <Container className="py-4" style={themeStyles}>
      <Row className="justify-content-center">
        <Col md={10}>
          {/* Create Team Card */}
          <Fade in appear>
            <Card 
              className="mb-4"
              style={cardThemeStyles}
            >
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <TbArrowsExchange size={24} className="me-2" />
                  <h4 className="mb-0">Create New Team</h4>
                </div>
                <Form onSubmit={handleCreateTeam}>
                  <FloatingLabel controlId="teamName" label="Team Name" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Enter team name"
                      value={team.name}
                      onChange={(e) => setTeam({ ...team, name: e.target.value })}
                      required
                      style={inputThemeStyles}
                    />
                  </FloatingLabel>
                  <FloatingLabel controlId="teamDescription" label="Description (Optional)">
                    <Form.Control
                      as="textarea"
                      placeholder="Optional team description"
                      value={team.description}
                      onChange={(e) => setTeam({ ...team, description: e.target.value })}
                      style={{ ...inputThemeStyles, height: '100px' }}
                    />
                  </FloatingLabel>
                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={teamLoading}
                      style={buttonHoverStyle}
                    >
                      {teamLoading ? (
                        <>
                          <Spinner size="sm" animation="border" className="me-2" />
                          Creating...
                        </>
                      ) : (
                        'Create Team'
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Fade>

          {/* Teams List */}
          {teams.map((team) => (
            <Fade in key={team.id}>
              <Card 
                className="mb-4"
                style={cardThemeStyles}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <TbUsersGroup size={20} className="me-2" />
                      <h5 className="mb-0">{team.name}</h5>
                      <Badge bg="secondary" className="ms-2">
                        {team.member_count} {team.member_count === 1 ? 'Member' : 'Members'}
                      </Badge>
                    </div>
                    <div>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Add Member</Tooltip>}
                      >
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          onClick={() => openAddMemberModal(team.id)}
                          style={buttonHoverStyle}
                        >
                          <TbUserPlus size={18} />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Delete Team</Tooltip>}
                      >
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => openDeleteTeamModal(team.id)}
                          style={buttonHoverStyle}
                        >
                          <TbTrash size={18} />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </div>
                  
                  {team.description && (
                    <div className="d-flex align-items-center text-muted mb-3">
                      <TbInfoCircle size={16} className="me-1" />
                      <small className={isDarkMode?"text-light":""}>{team.description}</small>
                    </div>
                  )}

                  {team.members.length > 0 ? (
                    <ListGroup variant="flush">
                      {team.members.map(member => (
                        <ListGroup.Item 
                          key={member.id} 
                          className="d-flex justify-content-between align-items-center py-3 px-0"
                          style={{ backgroundColor: 'transparent' }}
                        >
                          <div>
                            <div className={isDarkMode?"text-light fw-medium":"fw-medium"}>{member.name}</div>
                            <div className={isDarkMode?"small text-light":"small text-muted"}>
                              {member.email} • {member.role}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => openRemoveMemberModal(team.id, member)}
                            style={buttonHoverStyle}
                          >
                            <TbUserMinus size={16} />
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <TbUsersGroup size={32} className={isDarkMode?"text-light":"mb-2"} />
                      <p className={isDarkMode?"text-light mb-0":"mb-0"}>No members yet</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Fade>
          ))}
        </Col>
      </Row>
    </Container>
  );

  return (
    <ThemeProvider theme={theme}>
      <Container fluid className="py-4" style={themeStyles}>
        <Row className="justify-content-center">
          <Col xl={10} xxl={9}>
            <Card className="shadow-sm" style={cardThemeStyles}>
              <Card.Body className="p-0">
                <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                  <Nav variant="tabs" className="border-bottom">
                    <Nav.Item>
                      <Nav.Link 
                        eventKey="admins" 
                        className={`${activeTab === 'admins' ? 'active' : ''} ${isDarkMode ? 'text-light' : ''}`}
                        style={{ 
                          backgroundColor: activeTab === 'admins' ? ("var(--v2-surface)") : 'transparent',
                          borderColor: "var(--v2-border)"
                        }}
                      >
                        <FiUsers className="me-2" />
                        Admin Management
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link 
                        eventKey="teams" 
                        className={`${activeTab === 'teams' ? 'active' : ''} ${isDarkMode ? 'text-light' : ''}`}
                        style={{ 
                          backgroundColor: activeTab === 'teams' ? ("var(--v2-surface)") : 'transparent',
                          borderColor: "var(--v2-border)"
                        }}
                      >
                        <TbUsersGroup className="me-2" />
                        Team Management
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                  
                  <Tab.Content>
                    <Tab.Pane eventKey="admins" className="p-0">
                      {renderAdminManagement()}
                    </Tab.Pane>
                    <Tab.Pane eventKey="teams" className="p-0">
                      {renderTeamManagement()}
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Admin Delete Confirmation Modal */}
        <Modal
          show={showDeleteAdminModal}
          onHide={() => setShowDeleteAdminModal(false)}
          centered
          backdrop="static"
          size={isMobile ? "sm" : "md"}
        >
          <Modal.Header 
            closeButton 
            closeVariant={isDarkMode ? "white" : undefined}
            style={cardThemeStyles}
          >
            <Modal.Title style={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
              <FiTrash2 className="me-2" />
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={cardThemeStyles}>
            <p style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
              Are you sure you want to delete admin account <strong>{adminToDelete?.username}</strong>?
              This action cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer style={cardThemeStyles}>
            <Button 
              variant="secondary" 
              onClick={() => setShowDeleteAdminModal(false)}
              disabled={adminLoading}
              size={isMobile ? "sm" : undefined}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteAdmin}
              disabled={adminLoading}
              size={isMobile ? "sm" : undefined}
            >
              {adminLoading ? (
                <Spinner size="sm" animation="border" className="me-2" />
              ) : null}
              Delete Admin
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add Member Modal */}
        <Modal 
          show={showAddMemberModal} 
          onHide={() => setShowAddMemberModal(false)}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton style={cardThemeStyles}>
            <Modal.Title className="d-flex align-items-center">
              <TbUserPlus className="me-2" /> Add Team Member
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={cardThemeStyles}>
            <Form onSubmit={handleAddMember}>
              <FloatingLabel controlId="memberName" label="Full Name" className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Enter member name"
                  value={member.name}
                  onChange={(e) => setMember({ ...member, name: e.target.value })}
                  required
                  style={inputThemeStyles}
                />
              </FloatingLabel>
              <FloatingLabel controlId="memberEmail" label="Email Address" className="mb-3">
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={member.email}
                  onChange={(e) => setMember({ ...member, email: e.target.value })}
                  required
                  style={inputThemeStyles}
                />
              </FloatingLabel>
              <FloatingLabel controlId="memberRole" label="Role/Position">
                <Form.Control
                  type="text"
                  placeholder="Enter role"
                  value={member.role}
                  onChange={(e) => setMember({ ...member, role: e.target.value })}
                  required
                  style={inputThemeStyles}
                />
              </FloatingLabel>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setShowAddMemberModal(false)}
                  style={buttonHoverStyle}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={teamLoading}
                  style={buttonHoverStyle}
                >
                  {teamLoading ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Adding...
                    </>
                  ) : (
                    'Add Member'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Delete Team Modal */}
        <Modal 
          show={showDeleteTeamModal} 
          onHide={() => setShowDeleteTeamModal(false)}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton style={cardThemeStyles}>
            <Modal.Title className="d-flex align-items-center">
              <TbTrash className="me-2" /> Delete Team
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={cardThemeStyles}>
            <div className="text-center mb-4">
              <TbTrash size={48} className="text-danger mb-3" />
              <h5>Are you sure you want to delete this team?</h5>
              <p className="text-muted">
                This will permanently remove the team and all its members. This action cannot be undone.
              </p>
            </div>
            <div className="d-flex justify-content-center gap-3">
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowDeleteTeamModal(false)}
                style={buttonHoverStyle}
              >
                Cancel
              </Button>
              <Button 
                variant="danger" 
                onClick={handleDeleteTeam}
                disabled={teamLoading}
                style={buttonHoverStyle}
              >
                {teamLoading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Team'
                )}
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Remove Member Modal */}
        <Modal 
          show={showRemoveMemberModal} 
          onHide={() => setShowRemoveMemberModal(false)}
          centered
          backdrop="static"
          size="md"
        >
          <Modal.Header closeButton style={cardThemeStyles}>
            <Modal.Title className="d-flex align-items-center">
              <TbUserMinus className="me-2" /> Remove Member
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={cardThemeStyles}>
            <div className="text-center mb-4">
              <TbUserMinus size={48} className="text-warning mb-3" />
              <h5>Remove {selectedMember?.name}?</h5>
              <p className="text-muted">
                This will remove the member from the team. They can be added again later if needed.
              </p>
            </div>
            <div className="d-flex justify-content-center gap-3">
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowRemoveMemberModal(false)}
                style={buttonHoverStyle}
              >
                Cancel
              </Button>
              <Button 
                variant="warning" 
                onClick={handleRemoveMember}
                disabled={teamLoading}
                style={buttonHoverStyle}
              >
                {teamLoading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Removing...
                  </>
                ) : (
                  'Remove Member'
                )}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Container>
    </ThemeProvider>
  );
}

export default UserManagement;
